import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ─── Brand ───────────────────────────────────────────────────────
const Y  = "#FFD700";
const YD = "#E6B800";
const YB = "rgba(255,215,0,0.10)";
const YBR = "rgba(255,215,0,0.28)";

const fadeUp = {
  hidden:  { opacity:0, y:36 },
  visible: (i=0) => ({ opacity:1, y:0, transition:{ delay:i*0.09, type:"spring", stiffness:240, damping:26 } }),
};
const scaleIn = {
  hidden:  { opacity:0, scale:0.88 },
  visible: (i=0) => ({ opacity:1, scale:1, transition:{ delay:i*0.07, type:"spring", stiffness:280, damping:28 } }),
};

// ─── Data ────────────────────────────────────────────────────────
const NAV = [
  { label:"Campus",    href:"#campus"    },
  { label:"Estrellas", href:"#estrellas" },
  { label:"Semanas",   href:"#semanas"   },
  { label:"Precios",   href:"#precios"   },
  { label:"La Sede",   href:"#sede"      },
  { label:"Inscríbete",href:"#reserva",  cta:true },
];

const STATS = [
  { num:"6",    label:"Semanas temáticas" },
  { num:"+200", label:"Jugadores por edición" },
  { num:"NBA",  label:"Metodología de élite" },
  { num:"100%", label:"Satisfacción familias" },
];

const SEMANAS = [
  { num:"01", name:"Flow Camp",              desc:"Fluidez ofensiva y juego en movimiento.",                          dates:"23–27 Jun", color:"#FF6B6B" },
  { num:"02", name:"Timechamber Basics",     desc:"Fundamentos sólidos para un juego de élite.",                     dates:"30 Jun–4 Jul", color:"#4ECDC4" },
  { num:"03", name:"Shooting Academy",       desc:"Mecánica, ritmo y confianza de tiro.",                            dates:"7–11 Jul",  color:"#45B7D1" },
  { num:"04", name:"One on One Mastery",     desc:"Domina el 1v1. Fintas y creatividad ofensiva.",                   dates:"14–18 Jul", color:"#A78BFA" },
  { num:"05", name:"Ballhandling Lab",       desc:"Control de balón al máximo nivel.",                               dates:"21–25 Jul", color:"#F59E0B" },
  { num:"06", name:"Skills Lab Experience", desc:"Integración total. El broche de oro.",                             dates:"28 Jul–1 Ago", color:Y },
];

const STARS = [
  {
    name:"Ainhoa López", role:"Jugadora profesional · Selección Española", emoji:"🏀",
    color:"#FF6B9D", ig:"ainhoalopez_official",
    posts:[
      { code:"C-0jXXDMY5r", type:"reel" },
      { code:"DMcpUFgst4L", type:"post" },
    ],
  },
  {
    name:"Robert Willet", role:"NBA Trainer · @bballwillett", emoji:"💪",
    color:"#4ECDC4", ig:"bballwillett",
    posts:[
      { code:"DMqb9P8sUic", type:"reel" },
      { code:"DL0UkAfMJsI", type:"reel" },
    ],
  },
  {
    name:"Malak Shady", role:"Basketball Influencer · MVP 3×3", emoji:"👑",
    color:"#A78BFA", ig:"malakshady_22",
    posts:[
      { code:"DJXMrhzsqYg", type:"reel" },
      { code:"DFiwKWIMz2q", type:"reel" },
    ],
  },
];

const GALLERY = ["nau8.jpg","nau6.jpg","nau9.jpg","nau1.jpg","nau2.jpg","nau3.jpg","nau5.jpg","nau7.jpg"];

// ─── Countdown ───────────────────────────────────────────────────
function useCountdown(target: Date) {
  const calc = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { d:0, h:0, m:0, s:0 };
    return {
      d: Math.floor(diff/86400000),
      h: Math.floor((diff%86400000)/3600000),
      m: Math.floor((diff%3600000)/60000),
      s: Math.floor((diff%60000)/1000),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => { const id = setInterval(()=>setT(calc),1000); return ()=>clearInterval(id); }, []);
  return t;
}

function Countdown() {
  const t = useCountdown(new Date("2026-06-23T09:00:00"));
  const units = [
    { v:t.d, l:"días"    },
    { v:t.h, l:"horas"   },
    { v:t.m, l:"minutos" },
    { v:t.s, l:"segs"    },
  ];
  return (
    <div className="flex items-center gap-3 justify-center flex-wrap">
      {units.map(({v,l}) => (
        <div key={l} className="flex flex-col items-center">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center"
            style={{ background:"rgba(0,0,0,0.5)", border:`2px solid ${YBR}`, backdropFilter:"blur(8px)" }}>
            <span className="text-2xl md:text-3xl font-black tabular-nums" style={{ fontFamily:"var(--font-display)", color:Y }}>
              {String(v).padStart(2,"0")}
            </span>
          </div>
          <span className="text-xs text-white/50 mt-1 uppercase tracking-widest">{l}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Floating particles ───────────────────────────────────────────
function Particles() {
  const balls = Array.from({length:12},(_,i)=>i);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex:1 }}>
      {balls.map(i => (
        <motion.div key={i}
          className="absolute w-2 h-2 rounded-full opacity-20"
          style={{ background:Y, left:`${8+i*7.5}%`, top:`${20+Math.sin(i)*30}%` }}
          animate={{ y:[-12,12,-12], opacity:[0.1,0.3,0.1] }}
          transition={{ duration:3+i*0.4, repeat:Infinity, ease:"easeInOut", delay:i*0.3 }}
        />
      ))}
    </div>
  );
}

// ─── Parallax divider ────────────────────────────────────────────
function ParallaxDivider({ img, text }: { img:string; text:string }) {
  return (
    <div className="relative h-52 overflow-hidden flex items-center justify-center"
      style={{ backgroundImage:`url(/images/${img})`, backgroundSize:"cover", backgroundAttachment:"fixed", backgroundPosition:"center" }}>
      <div className="absolute inset-0 bg-black/70" />
      <motion.p initial={{ opacity:0, letterSpacing:"0.6em" }} whileInView={{ opacity:1, letterSpacing:"0.15em" }}
        viewport={{ once:true }} transition={{ duration:1.2 }}
        className="relative z-10 text-3xl md:text-5xl font-black uppercase tracking-widest text-center"
        style={{ fontFamily:"var(--font-display)", color:Y }}>
        {text}
      </motion.p>
    </div>
  );
}

// ─── Stats bar ───────────────────────────────────────────────────
function StatsBar() {
  return (
    <div className="py-10 border-y border-border" style={{ background:`linear-gradient(90deg, rgba(255,215,0,0.04) 0%, rgba(255,215,0,0.10) 50%, rgba(255,215,0,0.04) 100%)` }}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s,i) => (
            <motion.div key={s.label} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once:true }} custom={i}
              className="text-center">
              <div className="text-4xl md:text-5xl font-black mb-1" style={{ fontFamily:"var(--font-display)", color:Y }}>{s.num}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Masonry gallery ─────────────────────────────────────────────
function MasonryGallery() {
  const cols = [
    [GALLERY[0], GALLERY[3], GALLERY[6]],
    [GALLERY[1], GALLERY[4], GALLERY[7]],
    [GALLERY[2], GALLERY[5]],
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {cols.map((col, ci) => (
        <div key={ci} className="flex flex-col gap-3">
          {col.map((img, ri) => (
            <motion.div key={img} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once:true }} custom={ci*0.2+ri*0.1}
              className="rounded-xl overflow-hidden group cursor-pointer relative"
              style={{ aspectRatio: ri%2===0?"4/5":"1/1" }}>
              <img src={`/images/${img}`} alt="Campus Timechamber"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                style={{ background:"rgba(255,215,0,0.15)" }}>
                <span className="text-2xl">🏀</span>
              </div>
            </motion.div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Week timeline ───────────────────────────────────────────────
function WeekTimeline() {
  return (
    <div className="relative">
      {/* Línea central */}
      <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2"
        style={{ background:`linear-gradient(180deg, transparent, ${Y}, transparent)` }} />
      <div className="space-y-8 md:space-y-0">
        {SEMANAS.map((s, i) => {
          const isLeft = i%2===0;
          return (
            <motion.div key={s.num} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} custom={i*0.15}
              className={`relative grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12 items-center ${isLeft?"":"md:direction-rtl"}`}>
              {/* Dot central */}
              <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full items-center justify-center z-10"
                style={{ background:s.color, boxShadow:`0 0 16px ${s.color}` }} />
              {/* Card */}
              <div className={`${isLeft?"md:col-start-1":"md:col-start-2"} p-6 rounded-2xl border bg-card hover:-translate-y-1 transition-all duration-300`}
                style={{ borderColor:`${s.color}40`, boxShadow:`0 4px 24px -8px ${s.color}30` }}>
                <div className="flex items-start gap-3">
                  <span className="text-4xl font-black leading-none shrink-0" style={{ fontFamily:"var(--font-display)", color:s.color }}>{s.num}</span>
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest mb-1" style={{ color:s.color }}>{s.dates}</p>
                    <h3 className="text-xl font-black uppercase tracking-tight mb-2 text-card-foreground" style={{ fontFamily:"var(--font-display)" }}>{s.name}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </div>
              {/* Spacer para lado contrario */}
              <div className={`hidden md:block ${isLeft?"md:col-start-2":"md:col-start-1"}`} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Star card ───────────────────────────────────────────────────
function StarCard({ star }: { star: typeof STARS[0] }) {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).instgrm) (window as any).instgrm.Embeds.process();
  }, []);
  return (
    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }}
      className="rounded-3xl overflow-hidden border"
      style={{ borderColor:`${star.color}30`, background:`linear-gradient(135deg, ${star.color}08, transparent)`, boxShadow:`0 8px 40px -12px ${star.color}40` }}>
      {/* Header */}
      <div className="p-6 pb-4 flex items-center gap-4"
        style={{ borderBottom:`1px solid ${star.color}20` }}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl shrink-0"
          style={{ background:`${star.color}20`, border:`2px solid ${star.color}50` }}>
          {star.emoji}
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-black uppercase tracking-tight" style={{ fontFamily:"var(--font-display)", color:star.color }}>{star.name}</h3>
          <p className="text-muted-foreground text-xs">{star.role}</p>
        </div>
        <a href={`https://www.instagram.com/${star.ig}/`} target="_blank" rel="noopener noreferrer"
          className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all hover:scale-105"
          style={{ background:`${star.color}20`, color:star.color, border:`1px solid ${star.color}40` }}>
          @{star.ig}
        </a>
      </div>
      {/* Posts */}
      <div className="p-4 overflow-x-auto">
        <div className="flex gap-3" style={{ minWidth: "min-content" }}>
          {star.posts.map(p => {
            const path = p.type==="reel"?"reel":"p";
            return (
              <div key={p.code} className="w-[260px] shrink-0 rounded-xl overflow-hidden"
                style={{ border:`1px solid ${star.color}25`, boxShadow:`0 4px 16px rgba(0,0,0,0.4)` }}>
                <blockquote className="instagram-media"
                  data-instgrm-permalink={`https://www.instagram.com/${path}/${p.code}/?utm_source=ig_embed`}
                  data-instgrm-version="14"
                  style={{ background:"#000", border:0, boxShadow:"none", display:"block", margin:0, minWidth:"260px", padding:0, width:"100%" }}>
                  <div style={{ padding:"10px" }}>
                    <a href={`https://www.instagram.com/${path}/${p.code}/`} target="_blank" rel="noopener noreferrer"
                      style={{ color:star.color, display:"block", textAlign:"center", fontFamily:"var(--font-sans)", fontSize:"13px", padding:"8px 0", textDecoration:"none" }}>
                      {p.type==="reel"?"🎬":"📸"} Ver en Instagram →
                    </a>
                  </div>
                </blockquote>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Campus feed (#campustimechamber) ────────────────────────────
function CampusFeed() {
  useEffect(() => {
    const existing = document.querySelector('script[src*="instagram.com/embed"]');
    if (existing) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).instgrm) (window as any).instgrm.Embeds.process();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.instagram.com/embed.js"; s.async = true;
    document.body.appendChild(s);
  }, []);

  // Posts reales de @cbgrupbarna con #timechamber / campus
  const campusPosts = [
    { code:"DWrubLpDIU0", type:"reel" }, // Semana Santa Timechamber
    { code:"DSk2otLiDVo", type:"reel" }, // Campus en marxa
    { code:"DQ7nLmkjINq", type:"post" }, // TIMECHAMBER EXPERIENCE x GRUP BARNA
    { code:"DUtmAbHjFIs", type:"post" }, // TimechamberExperience GrupBarna
    { code:"DW2Iuj-jKwB", type:"post" }, // Timechamber Vibes
    { code:"DIb0HSfMud_", type:"post" }, // DAY 1 SKILLS LAB
  ];

  return (
    <div className="overflow-x-auto w-full pb-4">
      <div className="flex gap-4 justify-start md:justify-center flex-nowrap" style={{ minWidth:"min-content" }}>
        {campusPosts.map(({code,type}) => {
          const path = type==="reel"?"reel":"p";
          return (
            <div key={code} className="w-[260px] shrink-0 rounded-xl overflow-hidden"
              style={{ border:`1px solid ${YBR}`, boxShadow:"0 4px 20px rgba(0,0,0,0.45)" }}>
              <blockquote className="instagram-media"
                data-instgrm-permalink={`https://www.instagram.com/${path}/${code}/?utm_source=ig_embed`}
                data-instgrm-version="14"
                style={{ background:"#000", border:0, boxShadow:"none", display:"block", margin:0, minWidth:"260px", padding:0, width:"100%" }}>
                <div style={{ padding:"10px" }}>
                  <a href={`https://www.instagram.com/${path}/${code}/`} target="_blank" rel="noopener noreferrer"
                    style={{ color:Y, display:"block", textAlign:"center", fontFamily:"var(--font-sans)", fontSize:"13px", padding:"8px 0", textDecoration:"none" }}>
                    {type==="reel"?"🎬":"📸"} Ver en Instagram →
                  </a>
                </div>
              </blockquote>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Instagram feed card ─────────────────────────────────────────
function FeedSection() {
  useEffect(() => {
    const existing = document.querySelector('script[src*="instagram.com/embed"]');
    if (existing) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).instgrm) (window as any).instgrm.Embeds.process();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.instagram.com/embed.js"; s.async = true;
    document.body.appendChild(s);
  }, []);

  const posts = [
    { code:"DLA0wKxsM2c", type:"reel" },
    { code:"DWrubLpDIU0", type:"reel" },
    { code:"DNjFQX1Mm1X", type:"reel" },
    { code:"DL0UkAfMJsI", type:"reel" },
    { code:"DUtmAbHjFIs", type:"post" },
    { code:"DQ7nLmkjINq", type:"post" },
  ];

  return (
    <div className="overflow-x-auto w-full pb-4">
      <div className="flex gap-4 justify-start md:justify-center flex-nowrap" style={{ minWidth:"min-content" }}>
        {posts.map(({code,type}) => {
          const path = type==="reel"?"reel":"p";
          return (
            <div key={code} className="w-[260px] shrink-0 rounded-xl overflow-hidden"
              style={{ border:`1px solid ${YBR}`, boxShadow:"0 4px 20px rgba(0,0,0,0.45)" }}>
              <blockquote className="instagram-media"
                data-instgrm-permalink={`https://www.instagram.com/${path}/${code}/?utm_source=ig_embed`}
                data-instgrm-version="14"
                style={{ background:"#000", border:0, boxShadow:"none", display:"block", margin:0, minWidth:"260px", padding:0, width:"100%" }}>
                <div style={{ padding:"10px" }}>
                  <a href={`https://www.instagram.com/${path}/${code}/`} target="_blank" rel="noopener noreferrer"
                    style={{ color:Y, display:"block", textAlign:"center", fontFamily:"var(--font-sans)", fontSize:"13px", padding:"8px 0", textDecoration:"none" }}>
                    {type==="reel"?"🎬":"📸"} Ver en Instagram →
                  </a>
                </div>
              </blockquote>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Datasheet modal ─────────────────────────────────────────────
function DatasheetModal({ open, onClose }: { open:boolean; onClose:()=>void }) {
  useEffect(() => {
    const fn = (e:KeyboardEvent) => { if(e.key==="Escape") onClose(); };
    window.addEventListener("keydown",fn); return ()=>window.removeEventListener("keydown",fn);
  },[onClose]);
  if(!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}
      style={{ background:"rgba(0,0,0,0.88)" }}>
      <motion.div initial={{ opacity:0, scale:0.88 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0, scale:0.88 }}
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{ border:`1px solid ${YBR}`, boxShadow:`0 24px 80px rgba(255,215,0,0.25)` }}
        onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4" style={{ background:"#111", borderBottom:`1px solid ${YBR}` }}>
          <span className="font-black uppercase tracking-widest text-sm" style={{ color:Y, fontFamily:"var(--font-display)" }}>📄 Dossier Timechamber 2026</span>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl font-bold transition-colors">×</button>
        </div>
        <div className="bg-black/90"><img src="/images/hero-main.jpg" alt="Dossier" className="w-full object-cover" style={{ maxHeight:440 }}/></div>
        <div className="px-6 py-5 flex flex-wrap gap-3 items-center justify-between" style={{ background:"#111", borderTop:`1px solid ${YBR}` }}>
          <div>
            <p className="text-white font-bold text-sm">Timechamber Experience 2026</p>
            <p className="text-white/50 text-xs">CB Grup Barna · 23 Jun – 1 Ago</p>
          </div>
          <div className="flex gap-3">
            <a href="#reserva" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-black uppercase tracking-wider text-black transition-all hover:scale-105" style={{ background:Y }}>Inscríbete →</a>
            <a href="https://cbgrupbarna.com" target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider text-white border border-white/20 hover:bg-white/10 transition-all">Web oficial</a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── WhatsApp ────────────────────────────────────────────────────
function WA() {
  return (
    <a href="https://wa.me/34698425153?text=Hola%2C%20me%20interesa%20el%20Timechamber%20Experience%202026"
      target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
      style={{ background:"#25D366", boxShadow:"0 6px 24px rgba(37,211,102,0.5)" }}>
      <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
}

// ─── Navbar ──────────────────────────────────────────────────────
function Navbar({ top }: { top:number }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { const fn=()=>setScrolled(window.scrollY>60); window.addEventListener("scroll",fn); return ()=>window.removeEventListener("scroll",fn); },[]);
  return (
    <header className={`fixed left-0 right-0 z-40 transition-all duration-300 ${scrolled?"bg-background/96 border-b border-border shadow-xl":"bg-transparent"}`} style={{ top }}>
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <a href="#campus" className="flex items-center gap-1 flex-wrap">
          <span className="text-lg font-black tracking-tight uppercase leading-none text-foreground" style={{ fontFamily:"var(--font-display)" }}>GRUP BARNA</span>
          <span className="text-lg font-black leading-none" style={{ color:Y }}>×</span>
          <span className="text-lg font-black tracking-tight uppercase leading-none" style={{ fontFamily:"var(--font-display)", color:Y }}>TIMECHAMBER</span>
        </a>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map(item => item.cta
            ? <a key={item.label} href={item.href} className="ml-3 px-5 py-2 rounded-full text-sm font-black uppercase tracking-wider text-black transition-all hover:scale-105 active:scale-95" style={{ background:Y }}>{item.label}</a>
            : <a key={item.label} href={item.href} className="px-3 py-2 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all">{item.label}</a>
          )}
        </nav>
        <button className="md:hidden p-2 flex flex-col gap-1.5" onClick={()=>setOpen(!open)} aria-label="Menú">
          <span className={`block w-6 h-0.5 bg-foreground transition-all ${open?"rotate-45 translate-y-2":""}`}/>
          <span className={`block w-6 h-0.5 bg-foreground transition-all ${open?"opacity-0":""}`}/>
          <span className={`block w-6 h-0.5 bg-foreground transition-all ${open?"-rotate-45 -translate-y-2":""}`}/>
        </button>
      </div>
      {open&&(
        <div className="md:hidden bg-background/98 border-t border-border px-4 py-3 flex flex-col gap-1">
          {NAV.map(item=>(
            <a key={item.label} href={item.href} onClick={()=>setOpen(false)}
              className={`py-3 text-center text-sm font-black uppercase tracking-wider rounded-lg transition-all ${item.cta?"text-black":"text-muted-foreground hover:text-foreground hover:bg-muted/20"}`}
              style={item.cta?{background:Y}:{}}>{item.label}</a>
          ))}
        </div>
      )}
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════
// HOME
// ═══════════════════════════════════════════════════════════════════
export default function Home() {
  const [banner, setBanner] = useState(true);
  const [dossier, setDossier] = useState(false);
  const bh = banner ? 56 : 0;
  const PROMO_END = new Date('2026-04-20T23:59:59');
  const { d, h, m, s } = useCountdown(PROMO_END);
  const expired = d === 0 && h === 0 && m === 0 && s === 0;

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ─── TOP BANNER ─────────────────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {banner && !expired && (
          <div className="w-full flex items-center justify-between gap-2 px-3 py-2" style={{ background:Y, color:"#0a0a0a", minHeight:56 }}>
            {/* Texto izquierda */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 flex-1 min-w-0">
              <span className="text-xs font-black uppercase tracking-wide whitespace-nowrap">🏀 -10% · Reserva y paga en 3 plazos</span>
              <span className="hidden sm:inline opacity-30">|</span>
              <span className="text-xs font-bold opacity-70 whitespace-nowrap">💳 Solo una parte ahora</span>
            </div>
            {/* CONTADOR */}
            <div className="flex items-center gap-1 shrink-0">
              {[{ v: d, l:'d' }, { v: h, l:'h' }, { v: m, l:'m' }, { v: s, l:'s' }].map(({ v, l }) => (
                <div key={l} className="flex flex-col items-center justify-center w-9 h-9 rounded-lg text-center" style={{ background:'rgba(0,0,0,0.18)' }}>
                  <span className="text-sm font-black leading-none tabular-nums">{String(v).padStart(2,'0')}</span>
                  <span className="text-[9px] font-bold uppercase opacity-60 leading-none mt-0.5">{l}</span>
                </div>
              ))}
            </div>
            {/* CTA + cierre */}
            <div className="flex items-center gap-1.5 shrink-0 ml-1">
              <a href="#reserva" className="px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider hover:scale-105 transition-all whitespace-nowrap" style={{ background:"#0a0a0a", color:Y }}>Reservar →</a>
              <button onClick={()=>setBanner(false)} className="opacity-50 hover:opacity-100 text-lg leading-none font-bold transition-opacity" aria-label="Cerrar">×</button>
            </div>
          </div>
        )}
        <Navbar top={bh} />
      </div>

      {/* ─── HERO ───────────────────────────────────────────────── */}
      <section id="campus" className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 overflow-hidden"
        style={{ paddingTop:bh+80, backgroundImage:"url(/images/hero-main.jpg)", backgroundSize:"cover", backgroundPosition:"center top" }}>
        <div className="absolute inset-0 bg-black/74"/>
        <div className="absolute inset-0" style={{ background:"radial-gradient(ellipse 80% 55% at 50% 100%, rgba(255,215,0,0.2) 0%, transparent 65%)" }}/>
        <Particles />

        <div className="relative z-10 max-w-4xl mx-auto w-full">
          {/* Logos */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="flex items-center justify-center gap-5 mb-8 flex-wrap">
            <a href="https://www.instagram.com/cbgrupbarna/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
              <img src="/images/logo-grupbarna-new.png" alt="CB Grup Barna" className="h-14 w-auto object-contain drop-shadow-2xl"/>
            </a>
            <span className="text-white/40 text-4xl font-black" style={{ fontFamily:"var(--font-display)" }}>×</span>
            <a href="https://www.instagram.com/timechamber_es/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
              <img src="/images/logo-tc-captura.png" alt="Time Chamber" className="h-11 w-auto object-contain drop-shadow-2xl" style={{ filter:"invert(1)" }}/>
            </a>
          </motion.div>

          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="text-[clamp(4rem,12vw,9rem)] font-black tracking-tighter leading-none uppercase mb-5 text-white"
            style={{ fontFamily:"var(--font-display)" }}>
            <span style={{ color:Y }}>TIME</span>CHAMBER
          </motion.h1>

          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-2 font-medium">
            El campus de verano de baloncesto más innovador de Barcelona.
          </motion.p>
          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={3} className="text-white/50 text-sm mb-8">
            🗓️ Del 23 de junio al 1 de agosto · La Nau del Clot
          </motion.p>

          {/* Countdown */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mb-8">
            <p className="text-xs font-black uppercase tracking-widest mb-3" style={{ color:Y }}>⏱ Empieza en</p>
            <Countdown />
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={5} className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            <a href="#reserva" className="px-8 py-4 rounded-full text-lg font-black uppercase tracking-wider text-black transition-all hover:scale-105 active:scale-95"
              style={{ fontFamily:"var(--font-display)", background:Y, boxShadow:`0 6px 28px rgba(255,215,0,0.45)` }}>
              Inscríbete ahora
            </a>
            <a href="#semanas" className="px-8 py-4 rounded-full text-lg font-bold text-white border border-white/25 hover:bg-white/10 transition-all">
              Ver programa
            </a>
            <button onClick={()=>setDossier(true)} className="px-8 py-4 rounded-full text-lg font-bold text-black transition-all hover:scale-105" style={{ background:YD }}>
              📄 Dossier
            </button>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 animate-bounce text-xl">↓</div>
      </section>

      {/* ─── STATS BAR ──────────────────────────────────────────── */}
      <StatsBar />

      {/* ─── PARALLAX 1 ─────────────────────────────────────────── */}
      <ParallaxDivider img="campus-group.jpg" text="Barcelona · Summer 2026" />

      {/* ─── ESTRELLAS ──────────────────────────────────────────── */}
      <section id="estrellas" className="py-24 px-4">
        <div className="container mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-widest mb-3 block" style={{ color:Y }}>Visitas al campus</span>
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily:"var(--font-display)", color:Y }}>
              Estrellas <span className="text-foreground">del Campus</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Referentes NBA e influencers del baloncesto que han entrenado con nuestros jugadores.
            </p>
          </motion.div>
          {/* Ainhoa + Robert: 2 columnas lado a lado, posts apilados verticalmente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {STARS.slice(0,2).map(star => (
              <div key={star.name} className="rounded-3xl border overflow-hidden"
                style={{ borderColor:`${star.color}30`, background:`linear-gradient(135deg, ${star.color}08, transparent)`, boxShadow:`0 8px 40px -12px ${star.color}40` }}>
                {/* Header estrella */}
                <div className="p-5 pb-4 flex items-center gap-3" style={{ borderBottom:`1px solid ${star.color}20` }}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl shrink-0"
                    style={{ background:`${star.color}20`, border:`2px solid ${star.color}50` }}>{star.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-black uppercase tracking-tight" style={{ fontFamily:"var(--font-display)", color:star.color }}>{star.name}</h3>
                    <p className="text-muted-foreground text-xs truncate">{star.role}</p>
                  </div>
                </div>
                {/* 2 posts apilados */}
                <div className="p-4 space-y-3">
                  {star.posts.map(p => {
                    const path = p.type==="reel"?"reel":"p";
                    return (
                      <div key={p.code} className="rounded-xl overflow-hidden"
                        style={{ border:`1px solid ${star.color}25` }}>
                        <blockquote className="instagram-media"
                          data-instgrm-permalink={`https://www.instagram.com/${path}/${p.code}/?utm_source=ig_embed`}
                          data-instgrm-version="14"
                          style={{ background:"#000", border:0, boxShadow:"none", display:"block", margin:0, minWidth:"100%", padding:0, width:"100%" }}>
                          <div style={{ padding:"10px" }}>
                            <a href={`https://www.instagram.com/${path}/${p.code}/`} target="_blank" rel="noopener noreferrer"
                              style={{ color:star.color, display:"block", textAlign:"center", fontFamily:"var(--font-sans)", fontSize:"13px", padding:"6px 0", textDecoration:"none" }}>
                              {p.type==="reel"?"🎬":"📸"} Ver en Instagram →
                            </a>
                          </div>
                        </blockquote>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {/* Malak Shady */}
          <div className="max-w-2xl mx-auto">
            <StarCard star={STARS[2]} />
          </div>
        </div>
      </section>

      {/* ─── PARALLAX 2 ─────────────────────────────────────────── */}
      <ParallaxDivider img="nau6.jpg" text="6 semanas · 6 experiencias" />

      {/* ─── SEMANAS TIMELINE ───────────────────────────────────── */}
      <section id="semanas" className="py-24 px-4">
        <div className="container mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-widest mb-3 block" style={{ color:Y }}>Programa</span>
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily:"var(--font-display)", color:Y }}>
              6 Semanas de <span className="text-foreground">Excelencia</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">Cada semana tiene identidad propia. Apúntate a una o ven todas.</p>
          </motion.div>
          <WeekTimeline />
        </div>
      </section>

      {/* ─── GALERÍA #campustimechamber ──────────────────────────── */}
      <section className="py-16 px-4 border-t border-border" style={{ background:`linear-gradient(180deg, transparent, ${YB} 50%, transparent)` }}>
        <div className="container mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="text-center mb-10">
            <span className="text-xs font-black uppercase tracking-widest mb-3 block" style={{ color:Y }}>#campustimechamber</span>
            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily:"var(--font-display)", color:Y }}>
              Así es el <span className="text-foreground">Campus</span>
            </h2>
          </motion.div>
          <CampusFeed />
        </div>
      </section>

      {/* ─── INSTAGRAM FEED ─────────────────────────────────────── */}
      <section className="py-20 px-4 border-t border-border">
        <div className="container mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="text-center mb-12">
            <span className="text-xs font-black uppercase tracking-widest mb-3 block" style={{ color:Y }}>Síguenos</span>
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-3" style={{ fontFamily:"var(--font-display)", color:Y }}>
              @cbgrupbarna
            </h2>
            <p className="text-muted-foreground text-sm">#campustimechamber · #timechamber</p>
          </motion.div>
          <FeedSection />
          <div className="text-center mt-8">
            <a href="https://www.instagram.com/cbgrupbarna/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider transition-all hover:scale-105"
              style={{ border:`1px solid ${YBR}`, color:Y, background:YB }}>
              Ver perfil completo →
            </a>
          </div>
        </div>
      </section>

      {/* ─── PRECIOS ────────────────────────────────────────────── */}
      <section id="precios" className="py-24 px-4 border-t border-border" style={{ background:`linear-gradient(180deg, transparent, ${YB} 50%, transparent)` }}>
        <div className="container mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-widest mb-3 block" style={{ color:Y }}>Inversión</span>
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter" style={{ fontFamily:"var(--font-display)", color:Y }}>
              Precios <span className="text-foreground">Claros</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            {[
              { title:"Semana Completa", price:"195€", detail:"9h a 17h", badge:"✓ Almuerzo incluido", highlight:true },
              { title:"Media Jornada",   price:"160€", detail:"9h a 13:30h", badge:null, highlight:false },
            ].map((plan,i)=>(
              <motion.div key={plan.title} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once:true }} custom={i}
                className="p-10 rounded-2xl border transition-all hover:-translate-y-2 duration-300 relative overflow-hidden"
                style={plan.highlight?{ borderColor:YD, background:YB, boxShadow:`0 8px 40px -8px rgba(255,215,0,0.35)` }:{ borderColor:"var(--border)", background:"var(--card)" }}>
                {plan.highlight && (
                  <div className="absolute top-0 right-0 w-24 h-24 opacity-5" style={{ background:Y, borderRadius:"0 0 0 100%" }}/>
                )}
                {plan.highlight && <span className="inline-block text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 text-black" style={{ background:Y }}>⭐ Recomendado</span>}
                <h3 className="text-2xl font-black uppercase tracking-tight mb-2 text-card-foreground" style={{ fontFamily:"var(--font-display)" }}>{plan.title}</h3>
                <div className="text-6xl font-black mb-2" style={{ fontFamily:"var(--font-display)", color:Y }}>{plan.price}</div>
                <p className="text-muted-foreground text-sm">{plan.detail}</p>
                {plan.badge && <p className="text-xs font-semibold mt-2" style={{ color:Y }}>{plan.badge}</p>}
                <a href="#reserva" className="mt-6 block w-full py-3 rounded-full text-sm font-black uppercase tracking-wider text-center text-black transition-all hover:scale-105"
                  style={{ background:plan.highlight?Y:"rgba(255,215,0,0.15)", color:plan.highlight?"#0a0a0a":Y, border:plan.highlight?"none":`1px solid ${YBR}` }}>
                  Reservar plaza →
                </a>
              </motion.div>
            ))}
          </div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
            <div className="inline-flex flex-col items-center gap-2 px-8 py-5 rounded-2xl" style={{ background:Y, color:"#0a0a0a" }}>
              <span className="text-base font-black uppercase tracking-wide">🔥 10% dto antes del 20 de abril</span>
              <span className="text-sm font-bold opacity-80">Código socios: <code className="font-black tracking-widest">BARNAAVIAT</code></span>
            </div>
            {/* PAGO A PLAZOS */}
            <div className="inline-flex flex-col items-center gap-3 px-8 py-5 rounded-2xl border-2" style={{ background:"rgba(255,215,0,0.06)", borderColor:YBR, color:"var(--foreground)" }}>
              <span className="text-sm font-black uppercase tracking-wide" style={{ color:Y }}>💳 Reserva y paga en 3 plazos</span>
              <div className="flex items-center gap-2 text-xs font-bold">
                <span className="px-2.5 py-1 rounded-full text-black" style={{ background:Y }}>1er pago</span>
                <span className="opacity-40">→</span>
                <span className="px-2.5 py-1 rounded-full border" style={{ borderColor:YBR }}>2º pago</span>
                <span className="opacity-40">→</span>
                <span className="px-2.5 py-1 rounded-full border" style={{ borderColor:YBR }}>3er pago</span>
              </div>
              <span className="text-xs opacity-70 text-center">Solo abonas una parte ahora para reservar tu plaza</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── PARALLAX 3 ─────────────────────────────────────────── */}
      <ParallaxDivider img="nau9.jpg" text="La Nau del Clot · Barcelona" />

      {/* ─── LA SEDE ────────────────────────────────────────────── */}
      <section id="sede" className="py-24 px-4">
        <div className="container mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="text-center mb-14">
            <span className="text-xs font-black uppercase tracking-widest mb-4 block" style={{ color:Y }}>Instalación oficial</span>
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily:"var(--font-display)", color:Y }}>
              La <span className="text-foreground">Sede</span>
            </h2>
            <p className="text-muted-foreground text-lg">La Nau Del Clot · Carrer de la Llacuna 172, Barcelona</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 items-start">
            {/* Reel */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="flex justify-center">
              <div className="w-full max-w-[400px] rounded-2xl overflow-hidden"
                style={{ boxShadow:`0 20px 60px -8px rgba(255,215,0,0.3)`, border:`1px solid ${YBR}` }}>
                <blockquote className="instagram-media"
                  data-instgrm-permalink="https://www.instagram.com/reel/DLA0wKxsM2c/?utm_source=ig_embed"
                  data-instgrm-version="14"
                  style={{ background:"#000", border:0, boxShadow:"none", display:"block", margin:0, minWidth:"326px", padding:0, width:"100%" }}>
                  <div style={{ padding:"16px" }}>
                    <a href="https://www.instagram.com/reel/DLA0wKxsM2c/" target="_blank" rel="noopener noreferrer"
                      style={{ color:Y, display:"block", textAlign:"center", fontFamily:"var(--font-sans)", fontSize:"14px", padding:"8px 0", textDecoration:"none" }}>
                      🎬 Ver Reel en Instagram →
                    </a>
                  </div>
                </blockquote>
              </div>
            </motion.div>
            {/* Info + mapa */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} custom={1} className="space-y-4">
              {[
                { icon:"📍", title:"Dirección",  desc:"Carrer de la Llacuna 172, 08018 Barcelona · La Nau Del Clot" },
                { icon:"🕘", title:"Horarios",   desc:"9h–13:30h (media jornada) · 9h–17h (jornada completa)" },
                { icon:"🏠", title:"Servicios",  desc:"Servicio de acogida · Excursión viernes Isla Fantasía · Almuerzo incluido" },
              ].map((item,i)=>(
                <div key={item.title} className="flex items-start gap-4 p-4 rounded-xl" style={{ border:`1px solid ${YBR}`, background:YB }}>
                  <span className="text-2xl shrink-0">{item.icon}</span>
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-wide mb-0.5" style={{ color:Y, fontFamily:"var(--font-display)" }}>{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Google Maps */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="rounded-2xl overflow-hidden" style={{ border:`1px solid ${YBR}`, boxShadow:`0 8px 32px rgba(0,0,0,0.4)` }}>
            <div className="px-6 py-4 flex items-center gap-3" style={{ background:"#111", borderBottom:`1px solid ${YBR}` }}>
              <span className="text-xl">📍</span>
              <div>
                <p className="font-black text-sm uppercase tracking-wider" style={{ color:Y }}>La Nau Del Clot</p>
                <p className="text-muted-foreground text-xs">Carrer de la Llacuna 172, 08018 Barcelona</p>
              </div>
              <a href="https://maps.google.com/?q=La+Nau+del+Clot+Carrer+de+la+Llacuna+172+Barcelona" target="_blank" rel="noopener noreferrer"
                className="ml-auto px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider text-black transition-all hover:scale-105" style={{ background:Y }}>
                Cómo llegar →
              </a>
            </div>
            <iframe title="La Nau del Clot"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2992.7942869506!2d2.1953!3d41.4079!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a30b6d8aaaab%3A0x5f2f3b7e4a8c1234!2sLa+Nau+del+Clot%2C+Carrer+de+la+Llacuna%2C+172%2C+08018+Barcelona!5e0!3m2!1ses!2ses!4v1713000000000!5m2!1ses!2ses"
              width="100%" height="380" style={{ border:"none", display:"block" }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"/>
          </motion.div>
        </div>
      </section>

      {/* ─── RESERVA / JOTFORM ──────────────────────────────────── */}
      <section id="reserva" className="py-24 px-4 border-t border-border" style={{ background:`linear-gradient(180deg, transparent, ${YB} 40%, transparent)` }}>
        <div className="container mx-auto max-w-3xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }} className="text-center mb-12">
            <span className="text-xs font-black uppercase tracking-widest mb-3 block" style={{ color:Y }}>Plazas limitadas</span>
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily:"var(--font-display)", color:Y }}>
              ¡Reserva tu <span className="text-foreground">Plaza!</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-4">Completa el formulario y asegura tu plaza en el campus más exclusivo de Barcelona.</p>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-black uppercase" style={{ background:Y, color:"#0a0a0a" }}>
              🔥 10% dto antes del 20 de abril
            </span>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }}>
            <div className="w-full rounded-2xl overflow-hidden" style={{ border:`1px solid ${YBR}`, boxShadow:`0 8px 40px rgba(255,215,0,0.12)` }}>
              <iframe title="Formulario Timechamber 2026" src="https://form.jotform.com/260962500106347"
                allow="geolocation; microphone; camera; fullscreen"
                style={{ width:"100%", height:"750px", border:"none", background:"transparent", display:"block" }} scrolling="yes"/>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── CONTACTO ───────────────────────────────────────────── */}
      <section id="contacto" className="py-20 px-4 border-t border-border">
        <div className="container mx-auto max-w-xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once:true }}>
            <h2 className="text-4xl font-black uppercase tracking-tighter mb-6" style={{ fontFamily:"var(--font-display)", color:Y }}>Contacto</h2>
            <p className="text-muted-foreground mb-8">¿Tienes dudas? Escríbenos por Instagram o WhatsApp.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://www.instagram.com/cbgrupbarna" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border font-bold text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-all"
                style={{ borderColor:YBR, color:Y, background:YB }}>📸 @cbgrupbarna</a>
              <a href="https://wa.me/34698425153" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-all"
                style={{ background:"#25D366", color:"#fff" }}>💬 WhatsApp</a>
            </div>
            <p className="mt-6 text-muted-foreground text-sm">🌐 <a href="https://cbgrupbarna.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">cbgrupbarna.com</a></p>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER ─────────────────────────────────────────────── */}
      <footer className="border-t border-border py-12" style={{ background:"rgba(255,215,0,0.03)" }}>
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-6 mb-8 flex-wrap">
            <a href="https://www.instagram.com/cbgrupbarna/" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity hover:scale-105 transform">
              <img src="/images/logo-grupbarna-new.png" alt="CB Grup Barna" className="h-12 w-auto object-contain"/>
            </a>
            <span className="text-white/20 text-2xl font-black">×</span>
            <a href="https://www.instagram.com/timechamber_es/" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity hover:scale-105 transform">
              <img src="/images/logo-tc-captura.png" alt="Timechamber" className="h-9 w-auto object-contain" style={{ filter:"invert(1)" }}/>
            </a>
          </div>
          <p className="text-3xl font-black tracking-tighter mb-1" style={{ fontFamily:"var(--font-display)", color:Y }}>TIMECHAMBER EXPERIENCE</p>
          <p className="text-muted-foreground text-sm mb-2">CB Grup Barna · Barcelona 2026</p>
          <p className="text-muted-foreground text-xs">La Nau Del Clot · Carrer de la Llacuna 172, Barcelona</p>
          <div className="flex justify-center gap-4 mt-6 flex-wrap">
            {["@cbgrupbarna","@timechamber_es","#timechamber","#campustimechamber"].map(tag=>(
              <span key={tag} className="text-xs text-muted-foreground/50 font-mono">{tag}</span>
            ))}
          </div>
          <p className="text-muted-foreground/30 text-xs mt-6">© 2026 CB Grup Barna · Todos los derechos reservados</p>
        </div>
      </footer>

      {/* ─── Dossier modal ──────────────────────────────────────── */}
      <AnimatePresence>{dossier && <DatasheetModal open={dossier} onClose={()=>setDossier(false)}/>}</AnimatePresence>

      {/* ─── WhatsApp ────────────────────────────────────────────── */}
      <WA />
    </div>
  );
}
